
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { modelsAPI, type ModelFilterOptions } from '@/lib/api/models'
import { useModelCatalog } from '@/hooks/useModelCatalog'
import { useLibrary } from '@/hooks/useLibrary'
import { hasHiddenNodeTypes, isHiddenNodeType } from './hidden-node-types'
import type { NodeTypeFilter, SortOption } from './library-types'

export interface UseLibrarySearchReturn {
  // Search
  search: string
  setSearch: (v: string) => void
  debouncedSearch: string

  // Filters
  activeNodeType: NodeTypeFilter
  setActiveNodeType: (v: NodeTypeFilter) => void
  category: string
  setCategory: (v: string) => void
  modelFamily: string
  setModelFamily: (v: string) => void
  sort: SortOption
  setSort: (v: SortOption) => void
  filterOptions: ModelFilterOptions
  hasActiveFilters: boolean
  clearFilters: () => void

  // Catalog
  models: ReturnType<typeof useModelCatalog>['models']
  totalCount: number
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  loadMore: () => void
  refetch: ReturnType<typeof useModelCatalog>['refetch']

  // Library membership
  isInLibrary: (modelId: string) => boolean
  toggleLibrary: (modelId: string) => Promise<void>
  bulkUpdate: ReturnType<typeof useLibrary>['bulkUpdate']

  // Infinite scroll sentinel
  sentinelRef: React.RefObject<HTMLDivElement | null>
}

export function useLibrarySearch(): UseLibrarySearchReturn {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [activeNodeType, setActiveNodeType] = useState<NodeTypeFilter>('')
  const [category, setCategory] = useState('')
  const [modelFamily, setModelFamily] = useState('')
  const [sort, setSort] = useState<SortOption>('featured')
  const [filterOptions, setFilterOptions] = useState<ModelFilterOptions>({ families: [], categories: [], nodeTypes: [], purposes: [] })

  const { models, totalCount, isLoading, isLoadingMore, hasMore, error, loadMore, refetch } = useModelCatalog({ skipInitialFetch: true })
  const { isInLibrary, toggleLibrary, bulkUpdate } = useLibrary()
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Fetch filter options on mount
  useEffect(() => {
    modelsAPI.getFilters().then(setFilterOptions).catch(() => {})
  }, [])

  // Debounce search — 300ms, identical to original
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const visibleModels = useMemo(
    () => (hasHiddenNodeTypes ? models.filter((model) => !isHiddenNodeType(model.nodeType)) : models),
    [models],
  )

  const visibleFilterOptions = useMemo<ModelFilterOptions>(
    () => (hasHiddenNodeTypes
      ? { ...filterOptions, nodeTypes: filterOptions.nodeTypes.filter((t) => !isHiddenNodeType(t)) }
      : filterOptions),
    [filterOptions],
  )

  const hasActiveFilters = !!(activeNodeType || category || modelFamily || sort !== 'featured')

  function clearFilters() {
    setActiveNodeType('')
    setCategory('')
    setModelFamily('')
    setSort('featured')
  }

  // IntersectionObserver for infinite scroll — identical cleanup
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  return {
    search,
    setSearch,
    debouncedSearch,
    activeNodeType,
    setActiveNodeType,
    category,
    setCategory,
    modelFamily,
    setModelFamily,
    sort,
    setSort,
    filterOptions: visibleFilterOptions,
    hasActiveFilters,
    clearFilters,
    models: visibleModels,
    totalCount,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refetch,
    isInLibrary,
    toggleLibrary,
    bulkUpdate,
    sentinelRef,
  }
}
